import React, { useState, useEffect } from 'react';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { offlineQueue } from '../services/offlineQueue';
import { offlineApiService } from '../services/offlineApi';

interface SyncStatusProps {
  className?: string;
}

const SyncStatus: React.FC<SyncStatusProps> = ({ className = '' }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingTasks, setPendingTasks] = useState<number>(0);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    const checkPendingTasks = async () => {
      const tasks = await offlineQueue.getAllTasks();
      setPendingTasks(tasks.length);
    };

    const handleOnline = () => {
      setIsOnline(true);
      checkPendingTasks();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    // Check initial status
    checkPendingTasks();

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check for pending tasks periodically
    const interval = setInterval(checkPendingTasks, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const handleSync = async () => {
    if (!isOnline || pendingTasks === 0 || isSyncing) return;

    setIsSyncing(true);
    try {
      const results = await offlineApiService.syncOfflineTasks();
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        // Show success notification
        console.log(`Synced ${successCount} tasks successfully`);
      }
      
      // Refresh pending task count
      const tasks = await offlineQueue.getAllTasks();
      setPendingTasks(tasks.length);
    } catch (error) {
      console.error('Sync failed:', error);
    } finally {
      setIsSyncing(false);
    }
  };

  if (!isOnline && pendingTasks === 0) {
    return (
      <div className={`flex items-center gap-2 text-amber-600 ${className}`}>
        <CloudOffIcon sx={{ width: 20, height: 20 }} />
        <span className="text-sm">Offline</span>
      </div>
    );
  }

  if (pendingTasks > 0) {
    return (
      <button
        onClick={handleSync}
        disabled={!isOnline || isSyncing}
        className={`flex items-center gap-2 ${
          isOnline ? 'text-blue-600 hover:text-blue-700' : 'text-gray-400'
        } ${className}`}
      >
        {isSyncing ? (
          <RefreshIcon sx={{ width: 20, height: 20, animation: 'spin 1s linear infinite' }} />
        ) : (
          <CloudIcon sx={{ width: 20, height: 20 }} />
        )}
        <span className="text-sm">
          {pendingTasks} pending {pendingTasks === 1 ? 'task' : 'tasks'}
        </span>
      </button>
    );
  }

  if (isOnline) {
    return (
      <div className={`flex items-center gap-2 text-green-600 ${className}`}>
        <CloudIcon sx={{ width: 20, height: 20 }} />
        <span className="text-sm">Synced</span>
      </div>
    );
  }

  return null;
};

export default SyncStatus;